import type { Candle } from "../types";

export type Timeframe = "M1" | "M5" | "M15" | "H1";

export const TIMEFRAME_SECONDS: Record<Timeframe, number> = {
  M1: 60,
  M5: 300,
  M15: 900,
  H1: 3600,
};

/** Deterministic PRNG so backtests are reproducible across reloads */
function mulberry32(seed: number) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function gauss(rand: () => number): number {
  const u = Math.max(rand(), 1e-12);
  const v = rand();
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}

/**
 * Generates a realistic XAUUSD candle history (random walk with
 * intrabar wicks + volume). Used until a live broker feed is wired in.
 */
export function generateDemoCandles(
  count: number,
  timeframe: Timeframe = "M1",
  seed = 42,
  startPrice = 2650.0
): Candle[] {
  const rand = mulberry32(seed);
  const step = TIMEFRAME_SECONDS[timeframe];
  const now = Math.floor(Date.now() / 1000);
  const startTime = now - count * step;

  // Volatility scales with timeframe (≈ sqrt of time)
  const tfMinutes = step / 60;
  const sigma = 0.00042 * Math.sqrt(tfMinutes);

  const candles: Candle[] = [];
  let price = startPrice;

  for (let i = 0; i < count; i++) {
    const open = price;
    const drift = 0.00001 * Math.sin(i / 90); // gentle trend component
    const shock = sigma * gauss(rand);
    const close = open * (1 + drift + shock);

    const wick = open * sigma * (0.4 + rand() * 0.9);
    const high = Math.max(open, close) + wick * rand();
    const low = Math.min(open, close) - wick * rand();

    candles.push({
      time: startTime + i * step,
      open: round2(open),
      high: round2(high),
      low: round2(low),
      close: round2(close),
      volume: Math.round(80 + rand() * 620),
    });

    price = close;
  }

  return candles;
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/**
 * Live feed adapter interface — implement with Finnhub / TwelveData /
 * FXCM WebSocket to replace the demo generator (Stage 1 §1.1).
 */
export interface MarketFeed {
  connect(onCandle: (candle: Candle) => void): () => void;
}


export async function fetchRealCandles(limit = 150): Promise<Candle[]> {
  const gateway = 'https://compatibility-grams-tone-outlets.trycloudflare.com/candles?limit=' + limit;
  const url = 'https://fapi.binance.com/fapi/v1/klines?symbol=XAUUSDT&interval=1m&limit=' + limit;
  const sources = [
    gateway,
    'https://api.allorigins.win/raw?url=' + encodeURIComponent(url),
    'https://r.jina.ai/https://fapi.binance.com/fapi/v1/klines?symbol=XAUUSDT&interval=1m&limit=' + limit,
  ];
  for (const p of sources) {
    try {
      const res = await fetch(p);
      if (!res.ok) continue;
      const txt = await res.text();
      const data = JSON.parse(txt);
      // gateway returns {candles: [...]}
      if (data && Array.isArray(data.candles)) {
        return data.candles.map((d: any) => ({
          time: Math.floor(d.time),
          open: parseFloat(d.open),
          high: parseFloat(d.high),
          low: parseFloat(d.low),
          close: parseFloat(d.close),
          volume: parseFloat(d.volume)
        }));
      }
      if (Array.isArray(data)) {
        return data.map(d => ({
          time: Math.floor(d[0] / 1000),
          open: parseFloat(d[1]),
          high: parseFloat(d[2]),
          low: parseFloat(d[3]),
          close: parseFloat(d[4]),
          volume: parseFloat(d[5])
        }));
      }
    } catch (e) {}
  }
  return [];
}


